import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';
import ActionCable from 'actioncable';
import { getToken } from '@/services/auth';
import { baseURL } from '@/services/api';

export const LiveChallengeContext = createContext();

export const LiveChallengeProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [timerSeconds, setTimerSeconds] = useState(30);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [myScore, setMyScore] = useState(null);
  const [participantCount, setParticipantCount] = useState(0);
  const [participants, setParticipants] = useState([]);
  const [answerCount, setAnswerCount] = useState({ answered: 0, total: 0 });
  const [sessionStatus, setSessionStatus] = useState(null);
  const [questionResult, setQuestionResult] = useState(null);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [lastAnswerFeedback, setLastAnswerFeedback] = useState(null);
  // True após broadcast question_closed; false após reveal de próxima Q ou reset.
  // Desabilita botões de resposta no aluno pra evitar respostas stale que chegariam
  // ao backend depois do current_question_index ter avançado.
  const [isQuestionClosed, setIsQuestionClosed] = useState(false);
  // Sinaliza para as telas do aluno (WaitingRoom/QuestionPage/AnswerFeedback)
  // que o admin reiniciou a sessão e elas devem navegar de volta ao /entrar.
  // É consumido uma vez e limpado via clearSessionReset.
  const [sessionReset, setSessionReset] = useState(false);
  const [isConnected, setIsConnected] = useState(false);

  const cableRef = useRef(null);
  const subscriptionsRef = useRef([]);
  const timerRef = useRef(null);

  const subscribe = useCallback((liveChallengeId, role) => {
    // Ao trocar de desafio (aluno fez join em outro), limpa estado e
    // subscriptions anteriores. Sem isso, sessionStatus='in_progress' do
    // desafio antigo permanece e WaitingRoom navega errado.
    subscriptionsRef.current.forEach(sub => sub?.unsubscribe());
    subscriptionsRef.current = [];
    if (cableRef.current) {
      cableRef.current.disconnect();
      cableRef.current = null;
    }

    setSessionReset(false);
    setCurrentQuestion(null);
    setQuestionIndex(0);
    setTotalQuestions(0);
    setHasAnswered(false);
    setIsQuestionClosed(false);
    setLastAnswerFeedback(null);
    setQuestionResult(null);
    setSessionStatus(null);
    setLeaderboard([]);
    setMyScore(null);
    setParticipantCount(0);
    setParticipants([]);
    setAnswerCount({ answered: 0, total: 0 });

    const token = getToken();
    const apiUrl = baseURL();
    const cableUrl = apiUrl.replace(/^http(s?):\/\/(.*?)\/api\/v1\/?$/, (_, s, host) => `ws${s}://${host}/cable`) + `?token=${encodeURIComponent(token)}`;

    cableRef.current = ActionCable.createConsumer(cableUrl);

    const channelParams = {
      channel: 'LiveChallengeChannel',
      live_challenge_id: liveChallengeId
    };

    const subscription = cableRef.current.subscriptions.create(channelParams, {
      received: message => handleMessage(message, role),
      connected: () => {
        setIsConnected(true);
        // Re-sync via REST após reconectar/F5: pega status + question_index +
        // tempo restante real. Sem isso o broadcast question_revealed (que só
        // é emitido 1x no reveal) é perdido e a UI fica com timer zerado ou
        // resetado pro valor inicial do timer_seconds.
        import('@/services/liveChallenges').then(({ liveChallengesApi }) => {
          liveChallengesApi
            .show(liveChallengeId)
            .then(response => {
              const attrs = response?.data?.attributes;
              if (!attrs) return;

              setSessionStatus(attrs.status);
              setParticipantCount(attrs['participants-count'] ?? 0);

              // Resync da questão ativa: se backend diz que está in_progress e
              // tem current-question, restaura currentQuestion + timer com o
              // tempo restante real (computado no servidor a partir do Redis).
              if (attrs.status === 'in_progress' && attrs['current-question']) {
                const remaining = attrs['time-remaining-seconds'] ?? 0;
                setCurrentQuestion(attrs['current-question']);
                setQuestionIndex(attrs['current-question-index'] ?? 0);
                setTotalQuestions(attrs['total-questions'] ?? 0);
                setTimerSeconds(attrs['timer-seconds']);
                if (remaining > 0) {
                  startTimer(remaining);
                } else {
                  // Questão já expirou; espera próximo broadcast (question_closed
                  // ou question_revealed da próxima Q).
                  setTimeRemaining(0);
                  setIsTimerRunning(false);
                }
              }
            })
            .catch(() => {
              // se REST falha, ActionCable ainda recebe próximo broadcast naturalmente
            });
        });
      },
      disconnected: () => setIsConnected(false)
    });

    subscriptionsRef.current.push(subscription);
  }, []);

  const unsubscribe = useCallback(() => {
    subscriptionsRef.current.forEach(sub => {
      if (sub) sub.unsubscribe();
    });
    subscriptionsRef.current = [];

    if (cableRef.current) {
      cableRef.current.disconnect();
      cableRef.current = null;
    }

    stopTimer();
  }, []);

  const handleMessage = useCallback(
    (message, role) => {
      const { type, data } = message;

      switch (type) {
        case 'participant_joined':
          // Não incrementamos count aqui — o broadcast `participant_count` paralelo
          // (vindo do Redis incr no channel) já é a fonte canônica. Incrementar aqui
          // causava dupla-contagem (um aluno entrava e virava 2 no painel do admin).
          setParticipants(prev => {
            const key = data?.user_id ?? data?.id ?? data?.name;
            if (key && prev.some(p => (p.user_id ?? p.id ?? p.name) === key)) return prev;
            return [...prev, data];
          });
          break;

        case 'participant_count':
          setParticipantCount(data.count);
          break;

        case 'back_to_lobby':
        case 'session_reset':
          // Admin reiniciou a sessão. Sem este handler o sessionStatus permanecia
          // 'in_progress' e o useEffect do ControlPanel snap-ava o phase de volta
          // para 'questionActive', fazendo a tela parecer que nada aconteceu.
          setSessionStatus('lobby');
          setCurrentQuestion(null);
          setQuestionResult(null);
          setQuestionIndex(0);
          setLeaderboard([]);
          setMyScore(null);
          setHasAnswered(false);
          setLastAnswerFeedback(null);
          setAnswerCount({ answered: 0, total: 0 });
          setParticipantCount(0);
          setParticipants([]);
          stopTimer();
          // Para alunos: marca o flag para que a tela atual (Aguardando/Questão/
          // Resposta) navegue de volta ao /entrar — o participante foi destruído
          // no backend, então não faz sentido continuar na sessão.
          if (role === 'student') setSessionReset(true);
          break;

        case 'answer_count_update':
          setAnswerCount({ answered: data.answered, total: data.total });
          break;

        case 'question_revealed':
          setCurrentQuestion(data.question);
          setQuestionIndex(data.question_index);
          setTotalQuestions(data.total_questions);
          setTimerSeconds(data.timer_seconds);
          setTimeRemaining(data.timer_seconds);
          setIsTimerRunning(true);
          setHasAnswered(false);
          setIsQuestionClosed(false);
          setLastAnswerFeedback(null);
          setQuestionResult(null);
          setAnswerCount({ answered: 0, total: answerCount.total });
          startTimer(data.timer_seconds);
          break;

        case 'question_closed':
          setIsTimerRunning(false);
          setIsQuestionClosed(true);
          stopTimer();
          setQuestionResult({
            correctAlternativeId: data.correct_alternative_id,
            answerDistribution: data.answer_distribution,
            leaderboard: data.leaderboard
          });
          if (data.leaderboard) {
            setLeaderboard(data.leaderboard);
          }
          break;

        case 'leaderboard_update':
          setLeaderboard(data);
          break;

        case 'session_started':
          setSessionStatus('in_progress');
          break;

        case 'session_finished':
          setSessionStatus('finished');
          setIsTimerRunning(false);
          stopTimer();
          if (data.leaderboard) {
            setLeaderboard(data.leaderboard);
          }
          break;

        default:
          break;
      }
    },
    [answerCount.total]
  );

  const startTimer = useCallback(seconds => {
    stopTimer();
    setTimeRemaining(seconds);
    setIsTimerRunning(true);

    timerRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          stopTimer();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setIsTimerRunning(false);
  }, []);

  useEffect(() => {
    return () => {
      unsubscribe();
      stopTimer();
    };
  }, []);

  const resetState = useCallback(() => {
    setSession(null);
    setCurrentQuestion(null);
    setQuestionIndex(0);
    setTotalQuestions(0);
    setLeaderboard([]);
    setMyScore(null);
    setParticipantCount(0);
    setParticipants([]);
    setAnswerCount({ answered: 0, total: 0 });
    setSessionStatus(null);
    setQuestionResult(null);
    setHasAnswered(false);
    setIsQuestionClosed(false);
    setLastAnswerFeedback(null);
    setSessionReset(false);
  }, []);

  const clearSessionReset = useCallback(() => setSessionReset(false), []);

  // Pausa timer quando aba vai pra background, retoma ao voltar.
  // Em mobile, browsers throttlam setInterval em background; pausar
  // evita drift e libera CPU/bateria.
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        if (timerRef.current) clearInterval(timerRef.current);
      } else if (isTimerRunning && timeRemaining > 0) {
        // Re-inicia interval; mas NÃO ajusta time (será confiado nas mensagens do channel).
        timerRef.current = setInterval(() => {
          setTimeRemaining(prev => Math.max(0, prev - 1));
        }, 1000);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [isTimerRunning, timeRemaining]);

  const value = {
    session,
    setSession,
    currentQuestion,
    questionIndex,
    totalQuestions,
    timerSeconds,
    timeRemaining,
    isTimerRunning,
    leaderboard,
    setLeaderboard,
    myScore,
    setMyScore,
    participantCount,
    participants,
    answerCount,
    sessionStatus,
    setSessionStatus,
    questionResult,
    hasAnswered,
    setHasAnswered,
    isQuestionClosed,
    lastAnswerFeedback,
    setLastAnswerFeedback,
    sessionReset,
    isConnected,
    clearSessionReset,
    subscribe,
    unsubscribe,
    resetState,
    startTimer,
    stopTimer
  };

  return <LiveChallengeContext.Provider value={value}>{children}</LiveChallengeContext.Provider>;
};
